import React, { useState, useEffect, useCallback } from 'react';
import {
  FiRefreshCw, FiPrinter, FiDownload, FiFileText,
  FiChevronDown, FiChevronRight,
  FiPackage, FiTrendingUp, FiAlertCircle, FiBarChart2,
  FiSearch, FiEdit3, FiList, FiX, FiArrowDownCircle, FiArrowUpCircle
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  getStockSummary, getStockByWarehouse, getStockByProduct,
  getStockMovementsSummary, getWarehouses, getFamilies, getEmpresaInfo,
  setWarehouseInitialStock, getMovements
} from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/ModuleTable.css';
import '../../../styles/StockInventario.css';

// ── Helpers de formato ──────────────────────────────────────
const fmtQ = (n, d = 3) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: d });
const fmtS = (n) =>
  `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = (n, d = 2) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d });
const todayStr = () => new Date().toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Lima' });
const firstOfMonth = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`;
};
const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ── Badge de estado de stock ────────────────────────────────
function BadgeStock({ status }) {
  const cfg = {
    NORMAL:    { bg: '#dcfce7', color: '#16a34a', border: '#86efac', label: 'NORMAL' },
    BAJO:      { bg: '#fef9c3', color: '#a16207', border: '#fde047', label: 'BAJO STOCK' },
    SIN_STOCK: { bg: '#fee2e2', color: '#dc2626', border: '#fca5a5', label: 'SIN STOCK' },
  };
  const s = cfg[status] || cfg.NORMAL;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap'
    }}>
      {s.label}
    </span>
  );
}

// ── Paginación ──────────────────────────────────────────────
function Pagination({ page, total, perPage, onPage, onPerPage }) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  return (
    <div className="mt-export-bar" style={{ justifyContent: 'space-between' }}>
      <span style={{ fontSize: 11, color: '#64748b' }}>
        {total} {total === 1 ? 'registro' : 'registros'}
      </span>
      <div className="mt-pagination">
        <button onClick={() => onPage(1)}            disabled={page === 1}>|&lt;</button>
        <button onClick={() => onPage(page - 1)}     disabled={page === 1}>&lt;</button>
        <span className="pg-current">{page}</span>
        <button onClick={() => onPage(page + 1)}     disabled={page >= totalPages}>&gt;</button>
        <button onClick={() => onPage(totalPages)}   disabled={page >= totalPages}>&gt;|</button>
        <span className="pg-info">de {totalPages}</span>
        <select value={perPage} onChange={e => { onPerPage(Number(e.target.value)); onPage(1); }}>
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / pág</option>)}
        </select>
      </div>
    </div>
  );
}

// ── Modal: establecer / editar stock inicial (sin movimientos aún) ──
function ModalAjusteInicial({ item, warehouseId, businessId, onClose, onSaved }) {
  const [stock,       setStock]       = useState('');
  const [stockUnidad, setStockUnidad] = useState('');
  const [err,   setErr]   = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setStock(String(parseFloat(item.stock || 0)));
      setStockUnidad(String(parseInt(item.stock_unidad || 0, 10)));
      setErr('');
    }
  }, [item]);

  if (!item) return null;
  const esEdicion = parseFloat(item.stock) !== 0 || parseInt(item.stock_unidad || 0, 10) !== 0;

  const save = async () => {
    const val = parseFloat(stock);
    if (isNaN(val) || val < 0) { setErr('Ingresa un valor numérico válido (≥ 0)'); return; }
    setSaving(true); setErr('');
    try {
      await setWarehouseInitialStock(warehouseId, item.id, businessId, {
        stock: val,
        stock_unidad: item.special_condition ? (parseInt(stockUnidad, 10) || 0) : 0,
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-modal-overlay">
      <div className="mt-modal" style={{ maxWidth: 440 }}>
        <div className="mt-modal-header">
          <span>{esEdicion ? 'Editar stock inicial' : 'Establecer stock inicial'}</span>
          <button className="btn-modal-close" onClick={onClose}><FiX size={14} /></button>
        </div>
        <div className="mt-modal-body">
          <div className="si-ajuste-producto">
            <div className="si-ajuste-producto-nombre">{item.product_name || item.name}</div>
            {item.sku && <div className="si-ajuste-producto-sku">Código: {item.sku}</div>}
          </div>

          <p className="si-ajuste-intro">
            {esEdicion
              ? 'Este producto todavía no registra ventas ni movimientos en este almacén, así que puedes corregir su cantidad libremente.'
              : 'Este producto es nuevo en el almacén. Cuéntanos cuánto stock tienes hoy para arrancar con el número correcto.'}
          </p>

          <div className="mt-form-grid" style={{ gridTemplateColumns: '1fr', gap: 16 }}>
            <div className="mt-field">
              <label>Cantidad en stock ({item.unit_measure || 'UND'})</label>
              <input
                type="number" min="0" step="0.01"
                value={stock}
                onChange={e => setStock(e.target.value)}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && !item.special_condition && save()}
              />
            </div>
            {!!item.special_condition && (
              <div className="mt-field">
                <label>Unidades en stock</label>
                <input
                  type="number" min="0" step="1"
                  value={stockUnidad}
                  onChange={e => setStockUnidad(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && save()}
                />
              </div>
            )}
          </div>

          <p className="si-ajuste-hint">
            💡 Una vez que este producto tenga su primera venta, compra o movimiento en este almacén,
            ya no podrás editarlo aquí — deberás usar el módulo de Movimientos para mantener el historial completo.
          </p>

          {err && <div className="mt-modal-error">{err}</div>}
        </div>
        <div className="mt-modal-footer">
          <button className="btn-modificar" onClick={save} disabled={saving}>
            {saving ? 'Guardando...' : esEdicion ? 'Actualizar' : 'Guardar stock inicial'}
          </button>
          <button className="btn-salir" onClick={onClose}>✕ Salir</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: historial de movimientos de un producto en un almacén ────
function ModalHistorialMovimientos({ item, warehouseId, warehouseName, onClose }) {
  const [movs,    setMovs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    getMovements({ warehouse_id: warehouseId, product_id: item.id })
      .then(r => setMovs(r.data?.data || []))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [warehouseId, item.id]);

  return (
    <div className="mt-modal-overlay">
      <div className="mt-modal" style={{ maxWidth: 820 }}>
        <div className="mt-modal-header">
          <span><FiList size={13} style={{ marginRight: 6 }} /> Historial — {item.product_name || item.name} ({warehouseName})</span>
          <button className="btn-modal-close" onClick={onClose}><FiX size={14} /></button>
        </div>
        <div className="mt-modal-body" style={{ maxHeight: 440, overflowY: 'auto' }}>
          {loading ? (
            <div className="mt-loading">Cargando...</div>
          ) : error ? (
            <div className="mt-modal-error">{error}</div>
          ) : movs.length === 0 ? (
            <div className="mt-loading">Sin movimientos registrados</div>
          ) : (
            <table className="mt-table">
              <thead>
                <tr className="mt-head-row">
                  <th>Fecha</th>
                  <th style={{ textAlign: 'center' }}>Tipo</th>
                  <th>Causa</th>
                  <th style={{ textAlign: 'right' }}>Cantidad</th>
                  <th style={{ textAlign: 'right' }}>Stock resultante</th>
                  <th>Usuario</th>
                  <th>Notas</th>
                </tr>
              </thead>
              <tbody>
                {movs.map((m, i) => {
                  const esEntrada = m.movement_type === 'ingreso';
                  return (
                    <tr key={m.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}
                      style={{ opacity: m.anulado ? 0.5 : 1, textDecoration: m.anulado ? 'line-through' : 'none' }}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{new Date(m.moved_at).toLocaleString('es-PE', { timeZone: 'America/Lima' })}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 8, fontWeight: 700, fontSize: 10.5,
                          background: esEntrada ? '#dcfce7' : '#fef3c7',
                          color: esEntrada ? '#166534' : '#92400e',
                        }}>
                          {esEntrada ? <FiArrowDownCircle size={10} /> : <FiArrowUpCircle size={10} />}
                          {m.movement_type?.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ fontSize: 11.5, color: '#64748b' }}>{m.causa || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: esEntrada ? '#166534' : '#b45309' }}>
                        {esEntrada ? '+' : '-'}{parseFloat(m.qty_moved).toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 11.5 }}>{parseFloat(m.stock_after).toFixed(2)}</td>
                      <td style={{ fontSize: 11.5, color: '#64748b' }}>{m.username || '—'}</td>
                      <td style={{ fontSize: 11, color: '#94a3b8', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.notes || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-modal-footer">
          <button className="btn-salir" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function StockInventario({ businessId }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab]       = useState('almacen');
  const [summary,   setSummary]         = useState(null);
  const [byWarehouse, setByWarehouse]   = useState([]);
  const [byProduct,   setByProduct]     = useState([]);
  const [movSummary,  setMovSummary]    = useState(null);
  const [warehouses,  setWarehouses]    = useState([]);
  const [families,    setFamilies]      = useState([]);
  const [empresa,     setEmpresa]       = useState(null);

  const [loading,    setLoading]        = useState(false);
  const [loadingMov, setLoadingMov]     = useState(false);

  // Filtros globales (por almacén y por producto)
  const [filters, setFilters]   = useState({ warehouse_id: '', family_id: '', stock_status: '', has_stock: '', search: '' });
  const [searchText, setSearchText] = useState('');

  // Filtros de movimientos
  const [movF, setMovF] = useState({
    from_date:    firstOfMonth(),
    to_date:      isoToday(),
    warehouse_id: ''
  });

  // UI
  const [expandedWh,   setExpandedWh]   = useState({});
  const [expandedProd, setExpandedProd] = useState({});
  const [colF,  setColF]  = useState({});
  const [page,  setPage]  = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [modalAjuste,    setModalAjuste]    = useState(null); // { item, warehouseId }
  const [modalHistorial, setModalHistorial] = useState(null); // { item, warehouseId, warehouseName }

  const today = todayStr();

  // ── Carga inicial: selectores + empresa ──────────────────
  const loadInit = useCallback(async () => {
    if (!businessId) return;
    try {
      const [wRes, fRes, eRes] = await Promise.all([
        getWarehouses(businessId),
        getFamilies(businessId),
        getEmpresaInfo(businessId)
      ]);
      setWarehouses(wRes.data?.data || []);
      setFamilies(fRes.data?.data   || []);
      setEmpresa(eRes.data?.data    || null);
    } catch {}
  }, [businessId]);

  // ── Carga de datos de stock ───────────────────────────────
  const loadData = useCallback(async (f) => {
    if (!businessId) return;
    const ef = f || filters;
    setLoading(true);
    try {
      const params = {};
      if (ef.warehouse_id) params.warehouse_id = ef.warehouse_id;
      if (ef.family_id)    params.family_id    = ef.family_id;
      if (ef.stock_status) params.stock_status = ef.stock_status;
      if (ef.has_stock)    params.has_stock    = ef.has_stock;
      if (ef.search)       params.search       = ef.search;

      const [sumRes, whRes, prodRes] = await Promise.all([
        getStockSummary(businessId),
        getStockByWarehouse(businessId, params),
        getStockByProduct(businessId, params)
      ]);

      setSummary(sumRes.data?.data      || null);
      const whData = whRes.data?.data   || [];
      const prData = prodRes.data?.data || [];
      setByWarehouse(whData);
      setByProduct(prData);

      // Expandir todos los almacenes por defecto
      const exp = {};
      whData.forEach(wh => { exp[wh.id] = true; });
      setExpandedWh(exp);
      setPage(1);
    } catch (err) {
      console.error('StockInventario.loadData:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId, filters]);

  // ── Carga de resumen de movimientos ──────────────────────
  const loadMovSummary = useCallback(async () => {
    if (!businessId) return;
    setLoadingMov(true);
    try {
      const res = await getStockMovementsSummary(businessId, movF);
      setMovSummary(res.data?.data || null);
    } catch (err) {
      console.error('StockInventario.loadMovSummary:', err);
    } finally {
      setLoadingMov(false);
    }
  }, [businessId, movF]);

  useEffect(() => { loadInit(); }, [loadInit]);
  useEffect(() => { loadData(); }, [businessId]); // carga inicial

  // Carga el resumen de movimientos la primera vez que se abre el tab
  useEffect(() => {
    if (activeTab === 'movimientos' && !movSummary) loadMovSummary();
  }, [activeTab]); // eslint-disable-line

  // ── Aplicar filtros ───────────────────────────────────────
  const handleFiltrar = () => {
    const f = { ...filters, search: searchText };
    setFilters(f);
    loadData(f);
  };

  // ── Filtros inline de columnas (Tab 2) ────────────────────
  const filteredProd = byProduct.filter(p => {
    if (colF.sku && !p.sku?.toLowerCase().includes(colF.sku.toLowerCase())) return false;
    if (colF.name    && !p.name?.toLowerCase().includes(colF.name.toLowerCase()))    return false;
    if (colF.family  && !p.family?.toLowerCase().includes(colF.family.toLowerCase())) return false;
    return true;
  });
  const pagedProd = filteredProd.slice((page - 1) * perPage, page * perPage);

  // ── Toggles ───────────────────────────────────────────────
  const toggleWh   = (id) => setExpandedWh(p   => ({ ...p, [id]: !p[id] }));
  const toggleProd = (id) => setExpandedProd(p => ({ ...p, [id]: !p[id] }));

  // ════════════════════════════════════════════════════════════
  // EXPORTACIONES
  // ════════════════════════════════════════════════════════════
  const empNombre = empresa?.name    || user?.business_name || '';
  const empRuc    = empresa?.ruc     || '';
  const empDir    = empresa?.address || empresa?.direccion || '';

  // ── PDF header helper ─────────────────────────────────────
  const pdfHeader = (doc, title, subtitle = '') => {
    doc.setFillColor(13, 27, 46);
    doc.rect(0, 0, doc.internal.pageSize.width, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text(empNombre, 14, 10);
    doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
    doc.text(`RUC: ${empRuc}  |  ${empDir}`, 14, 16);
    doc.setTextColor(14, 165, 233);
    doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 28);
    if (subtitle) {
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
      doc.text(subtitle, 14, 34);
    }
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(7);
    doc.text(`Generado: ${today}`, doc.internal.pageSize.width - 14, 28, { align: 'right' });
    return subtitle ? 40 : 34;
  };

  // ── Exportar Excel ────────────────────────────────────────
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Hoja 1: Resumen general
    if (summary) {
      const rows = [
        [`INVENTARIO DE STOCKS — ${empNombre}`],
        [`Generado:`, today],
        [],
        ['INDICADOR', 'VALOR'],
        ['Total Productos Activos', Number(summary.total_products)],
        ['Total Almacenes',         Number(summary.total_warehouses)],
        ['Valor Inventario (costo)', parseFloat(summary.total_valorizado)],
        ['Venta Proyectada',         parseFloat(summary.total_venta_proyectada)],
        ['Con Stock Normal',         Number(summary.stock_normal)],
        ['Bajo Stock',               Number(summary.stock_bajo)],
        ['Sin Stock',                Number(summary.sin_stock)],
        [],
        ['RESUMEN POR ALMACÉN'],
        ['Almacén', 'Total Productos', 'Con Stock', 'Valorizado (S/)', 'Venta Proyec. (S/)'],
        ...(summary.warehouses || []).map(w => [
          w.name, Number(w.total_products), Number(w.con_stock),
          parseFloat(w.valorizado), parseFloat(w.venta_proyectada)
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 35 }, { wch: 18 }, { wch: 15 }, { wch: 18 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Resumen');
    }

    // Hoja 2: Por Producto (consolidado)
    const prodRows = [
      ['Cod. Producto', 'Producto', 'Familia', 'UM', 'Stock Total', 'Precio Costo', 'Precio Venta', 'Valorizado', 'Venta Proyec.', 'Estado']
    ];
    byProduct.forEach(p => {
      const cod = p.sku || '';
      prodRows.push([
        cod, p.name, p.family || '',
        p.unit_measure, parseFloat(p.total_stock),
        parseFloat(p.cost_price), parseFloat(p.unit_price),
        parseFloat(p.total_valorizado), parseFloat(p.total_venta_proyectada),
        p.stock_status
      ]);
    });
    // Fila de totales
    const totVal = byProduct.reduce((s, p) => s + parseFloat(p.total_valorizado || 0), 0);
    const totVP  = byProduct.reduce((s, p) => s + parseFloat(p.total_venta_proyectada || 0), 0);
    prodRows.push([`TOTAL (${byProduct.length} productos)`, '', '', '', '', '', '', totVal, totVP, '']);
    const wsProd = XLSX.utils.aoa_to_sheet(prodRows);
    wsProd['!cols'] = [
      {wch:16},{wch:32},{wch:16},{wch:6},
      {wch:13},{wch:14},{wch:14},{wch:14},{wch:16},{wch:12}
    ];
    XLSX.utils.book_append_sheet(wb, wsProd, 'Por Producto');

    // Hoja 3: Por Almacén (detalle plano)
    const whRows = [
      ['Almacén', 'Cod. Producto', 'Producto', 'Familia', 'UM', 'Stock', 'Uds.', 'Precio Costo', 'Precio Venta', 'Valorizado', 'Venta Proyec.', 'Estado']
    ];
    byWarehouse.forEach(wh => {
      wh.products.forEach(p => {
        const cod = p.sku || '';
        whRows.push([
          wh.name, cod, p.product_name,
          p.family || '', p.unit_measure,
          parseFloat(p.stock), p.special_condition ? Number(p.stock_unidad) : '',
          parseFloat(p.cost_price), parseFloat(p.unit_price),
          parseFloat(p.valorizado), parseFloat(p.venta_proyectada), p.stock_status
        ]);
      });
      // Subtotal almacén
      whRows.push([`TOTAL ${wh.name}`, '', '', '', '', '', '', '', '', parseFloat(wh.valorizado), parseFloat(wh.venta_proyectada), '']);
      whRows.push([]);
    });
    const wsWh = XLSX.utils.aoa_to_sheet(whRows);
    wsWh['!cols'] = [
      {wch:22},{wch:16},{wch:32},{wch:16},{wch:6},
      {wch:12},{wch:8},{wch:14},{wch:14},{wch:14},{wch:16},{wch:12}
    ];
    XLSX.utils.book_append_sheet(wb, wsWh, 'Por Almacén');

    XLSX.writeFile(wb, `Inventario_${today.replace(/\//g, '-')}.xlsx`);
  };

  // ── Generar PDF ───────────────────────────────────────────
  const generatePDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    const whName = filters.warehouse_id
      ? warehouses.find(w => String(w.id) === String(filters.warehouse_id))?.name
      : 'Todos los almacenes';
    const subtitle = [
      filters.search        ? `Búsqueda: "${filters.search}"` : '',
      whName,
      filters.family_id     ? families.find(f => String(f.id) === String(filters.family_id))?.name : '',
      filters.stock_status  ? `Estado: ${filters.stock_status}` : ''
    ].filter(Boolean).join('  |  ');

    let y = pdfHeader(doc, 'INVENTARIO DE STOCKS — POR PRODUCTO', subtitle);

    // KPI row
    if (summary) {
      doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
      const kpis = [
        `Productos: ${summary.total_products}`,
        `Valorizado: ${fmtS(summary.total_valorizado)}`,
        `Venta proyectada: ${fmtS(summary.total_venta_proyectada)}`
      ];
      kpis.forEach((k, i) => doc.text(k, 14 + i * 90, y + 5));
      y += 12;
    }

    const totalVal = byProduct.reduce((s, p) => s + parseFloat(p.total_valorizado || 0), 0);
    const totalVP  = byProduct.reduce((s, p) => s + parseFloat(p.total_venta_proyectada || 0), 0);

    autoTable(doc, {
      startY: y,
      head: [['Cod. Producto', 'Producto', 'Familia', 'UM', 'Stock', 'P.Costo', 'P.Venta', 'Valorizado', 'V.Proyec.', 'Estado']],
      body: byProduct.map(p => {
        const cod = p.sku || '—';
        return [
          cod, p.name, p.family || '—',
          p.unit_measure, fmtQ(p.total_stock),
          `S/ ${fmtN(p.cost_price)}`, `S/ ${fmtN(p.unit_price)}`,
          `S/ ${fmtN(p.total_valorizado)}`, `S/ ${fmtN(p.total_venta_proyectada)}`,
          p.stock_status
        ];
      }),
      foot: [[
        `TOTAL (${byProduct.length})`, '', '', '', '', '', '',
        `S/ ${fmtN(totalVal)}`, `S/ ${fmtN(totalVP)}`, ''
      ]],
      styles:           { fontSize: 7.5, cellPadding: 2.5, textColor: [51, 65, 85] },
      headStyles:       { fillColor: [13, 27, 46], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      footStyles:       { fillColor: [241, 245, 249], textColor: [13, 27, 46], fontStyle: 'bold', fontSize: 7.5 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 60 },
        2: { cellWidth: 26 },
        3: { cellWidth: 12 },
        4: { cellWidth: 22, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 22, halign: 'right' },
        7: { cellWidth: 26, halign: 'right' },
        8: { cellWidth: 26, halign: 'right' },
        9: { cellWidth: 22 }
      },
      didDrawPage: (data) => {
        doc.setFontSize(7); doc.setTextColor(148, 163, 184);
        doc.text(
          `Pág. ${data.pageNumber}  —  ${empNombre}`,
          doc.internal.pageSize.width - 14,
          doc.internal.pageSize.height - 5,
          { align: 'right' }
        );
      }
    });

    return doc;
  };

  const exportPDF   = () => generatePDF().save(`Inventario_${today.replace(/\//g, '-')}.pdf`);
  const printReport = () => {
    const blob = generatePDF().output('blob');
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, '_blank');
    if (win) setTimeout(() => win.print(), 800);
  };

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════
  return (
    <div className="mt-container">

      {/* ── HEADER ── */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <span className="mt-title">INVENTARIO DE STOCKS</span>
          {summary && (
            <span style={{ fontSize: 11, color: '#64748b' }}>
              — {summary.total_products} productos · {summary.total_warehouses} almacenes
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{today}</span>
      </div>

      {/* ── KPI CARDS ── */}
      {summary && (
        <div className="si-kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="si-kpi si-kpi-blue">
            <div className="si-kpi-icon"><FiPackage /></div>
            <div>
              <div className="si-kpi-val">{summary.total_products}</div>
              <div className="si-kpi-label">Total Productos</div>
            </div>
          </div>
          <div className="si-kpi si-kpi-green">
            <div className="si-kpi-icon"><FiTrendingUp /></div>
            <div>
              <div className="si-kpi-val">{fmtS(summary.total_valorizado)}</div>
              <div className="si-kpi-label">Valor Inventario</div>
            </div>
          </div>
          <div className="si-kpi si-kpi-purple">
            <div className="si-kpi-icon"><FiBarChart2 /></div>
            <div>
              <div className="si-kpi-val">{fmtS(summary.total_venta_proyectada)}</div>
              <div className="si-kpi-label">Venta Proyectada</div>
            </div>
          </div>
        </div>
      )}

      {/* ── FILTER BAR ── */}
      <div className="si-filter-bar">
        <div className="si-filter-group">
          <div className="si-search-wrap">
            <FiSearch className="si-search-icon" />
            <input
              className="si-search"
              placeholder="Buscar producto..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleFiltrar()}
            />
          </div>
          <select className="si-select" value={filters.warehouse_id}
            onChange={e => setFilters(f => ({ ...f, warehouse_id: e.target.value }))}>
            <option value="">Todos los almacenes</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <select className="si-select" value={filters.family_id}
            onChange={e => setFilters(f => ({ ...f, family_id: e.target.value }))}>
            <option value="">Todas las familias</option>
            {families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select className="si-select" value={filters.has_stock}
            onChange={e => setFilters(f => ({ ...f, has_stock: e.target.value }))}>
            <option value="">Todos los productos</option>
            <option value="CON_STOCK">Con stock</option>
            <option value="SIN_STOCK">Sin stock</option>
          </select>
          <select className="si-select" value={filters.stock_status}
            onChange={e => setFilters(f => ({ ...f, stock_status: e.target.value }))}>
            <option value="">Todos los estados</option>
            <option value="NORMAL">Stock normal</option>
            <option value="BAJO">Bajo stock</option>
            <option value="SIN_STOCK">Sin stock</option>
          </select>
          <button className="btn-nuevo" onClick={handleFiltrar} disabled={loading}>
            <FiRefreshCw style={{ fontSize: 12 }} />
            {loading ? 'Cargando...' : 'Actualizar'}
          </button>
        </div>
        <div className="si-export-group">
          <button className="btn-excel" onClick={exportExcel} title="Exportar a Excel (.xlsx)">
            <FiDownload /> Excel
          </button>
          <button className="btn-pdf" onClick={exportPDF} title="Descargar PDF">
            <FiFileText /> PDF
          </button>
          <button className="btn-print" onClick={printReport} title="Imprimir">
            <FiPrinter /> Imprimir
          </button>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="si-tabs">
        {[
          { key: 'almacen',     label: 'Por Almacén' },
          { key: 'producto',    label: 'Por Producto' },
          { key: 'movimientos', label: 'Resumen Movimientos' }
        ].map(t => (
          <button key={t.key} className={`si-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-loading">Cargando inventario...</div>
      ) : (
        <>
          {/* ═══════════════════════════════════════════════════
              TAB 1 — POR ALMACÉN
          ═══════════════════════════════════════════════════ */}
          {activeTab === 'almacen' && (
            <div className="si-tab-content">
              {/* Mini cards resumen por almacén */}
              {summary?.warehouses?.length > 0 && (
                <div className="si-wh-cards">
                  {summary.warehouses.map(wh => (
                    <div key={wh.id} className="si-wh-card">
                      <div className="si-wh-card-name">{wh.name}</div>
                      <div className="si-wh-card-stats">
                        <span><b>{wh.total_products}</b> productos</span>
                        <span><b>{wh.con_stock}</b> con stock</span>
                      </div>
                      <div className="si-wh-card-val">{fmtS(wh.valorizado)}</div>
                      <div className="si-wh-card-vp">VP: {fmtS(wh.venta_proyectada)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Acordeón por almacén */}
              {byWarehouse.map(wh => (
                <div key={wh.id} className="si-wh-section">
                  <div className="si-wh-header" onClick={() => toggleWh(wh.id)}>
                    <div className="si-wh-header-left">
                      {expandedWh[wh.id] ? <FiChevronDown /> : <FiChevronRight />}
                      <span className="si-wh-name">{wh.name}</span>
                      {!!wh.is_default && <span className="si-badge-default">PRINCIPAL</span>}
                      <span className="si-wh-count">{wh.total_products} productos</span>
                    </div>
                    <div className="si-wh-header-right">
                      <span className="si-wh-val-label">Valorizado:</span>
                      <span className="si-wh-val">{fmtS(wh.valorizado)}</span>
                      <span className="si-wh-vp-label">Venta Proyec.:</span>
                      <span className="si-wh-vp">{fmtS(wh.venta_proyectada)}</span>
                    </div>
                  </div>

                  {expandedWh[wh.id] && (
                    <div className="mt-table-wrap">
                      <table className="mt-table">
                        <thead>
                          <tr className="mt-head-row">
                            <th>Cod. Producto</th>
                            <th>Producto</th>
                            <th>Familia</th>
                            <th>UM</th>
                            <th style={{ textAlign: 'right' }}>Stock</th>
                            <th style={{ textAlign: 'right' }}>UND</th>
                            <th style={{ textAlign: 'right' }}>P. Costo</th>
                            <th style={{ textAlign: 'right' }}>P. Venta</th>
                            <th style={{ textAlign: 'right' }}>Valorizado</th>
                            <th style={{ textAlign: 'right' }}>V. Proyec.</th>
                            <th>Estado</th>
                            <th style={{ textAlign: 'center' }}>Movimiento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wh.products.length === 0 ? (
                            <tr className="mt-empty">
                              <td colSpan={12}>Sin productos registrados en este almacén</td>
                            </tr>
                          ) : wh.products.map((p, i) => (
                            <tr key={p.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                              <td>
                                <span className="si-barcode">{p.sku || '—'}</span>
                              </td>
                              <td className="si-prod-name">{p.product_name}</td>
                              <td><span className="si-family-tag">{p.family || '—'}</span></td>
                              <td style={{ fontSize: 12, color: '#475569' }}>{p.unit_measure}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700,
                                           color: p.stock_status === 'SIN_STOCK' ? '#dc2626'
                                                : p.stock_status === 'BAJO' ? '#d97706' : '#1e293b' }}>
                                {fmtQ(p.stock)}
                              </td>
                              <td style={{ textAlign: 'right', color: '#64748b', fontSize: 11.5 }}>
                                {!!p.special_condition && p.stock_unidad > 0 ? p.stock_unidad : '—'}
                              </td>
                              <td style={{ textAlign: 'right', fontSize: 12 }}>S/ {fmtN(p.cost_price)}</td>
                              <td style={{ textAlign: 'right', fontSize: 12 }}>S/ {fmtN(p.unit_price)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                                {fmtS(p.valorizado)}
                              </td>
                              <td style={{ textAlign: 'right', color: '#7c3aed', fontWeight: 600 }}>
                                {fmtS(p.venta_proyectada)}
                              </td>
                              <td><BadgeStock status={p.stock_status} /></td>
                              <td style={{ textAlign: 'center' }}>
                                {p.has_movements ? (
                                  <button
                                    className="si-btn-mov si-btn-historial"
                                    onClick={() => setModalHistorial({ item: p, warehouseId: wh.id, warehouseName: wh.name })}
                                    title="Ver historial de movimientos"
                                  >
                                    <FiList size={11} /> Historial
                                  </button>
                                ) : (
                                  <button
                                    className="si-btn-mov si-btn-ajustar"
                                    onClick={() => setModalAjuste({ item: p, warehouseId: wh.id })}
                                    title="Establecer stock inicial"
                                  >
                                    <FiEdit3 size={11} /> Ajustar
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {wh.products.length > 0 && (
                          <tfoot>
                            <tr className="si-tfoot">
                              <td colSpan={8} style={{ textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#475569' }}>
                                TOTAL ALMACÉN:
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                                {fmtS(wh.valorizado)}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>
                                {fmtS(wh.venta_proyectada)}
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  )}
                </div>
              ))}

              {byWarehouse.length === 0 && !loading && (
                <div className="mt-loading">Sin almacenes activos.</div>
              )}

              {/* Total general */}
              {byWarehouse.length > 0 && (
                <div className="si-grand-total">
                  <span>TOTAL INVENTARIO (COSTO):</span>
                  <span className="si-grand-val">
                    {fmtS(byWarehouse.reduce((s, w) => s + parseFloat(w.valorizado || 0), 0))}
                  </span>
                  <span className="si-grand-sep">|</span>
                  <span>VENTA PROYECTADA:</span>
                  <span className="si-grand-vp">
                    {fmtS(byWarehouse.reduce((s, w) => s + parseFloat(w.venta_proyectada || 0), 0))}
                  </span>
                  <span className="si-grand-sep">|</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {byWarehouse.reduce((s, w) => s + w.total_products, 0)} líneas de stock
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════
              TAB 2 — POR PRODUCTO (consolidado)
          ═══════════════════════════════════════════════════ */}
          {activeTab === 'producto' && (
            <div className="si-tab-content">
              <Pagination
                page={page} total={filteredProd.length}
                perPage={perPage} onPage={setPage} onPerPage={setPerPage}
              />
              <div className="mt-table-wrap">
                <table className="mt-table">
                  <thead>
                    <tr className="mt-head-row">
                      <th style={{ width: 32 }}></th>
                      <th>Cod. Producto</th>
                      <th>Producto</th>
                      <th>Familia</th>
                      <th>UM</th>
                      <th style={{ textAlign: 'right' }}>Stock Total</th>
                      <th style={{ textAlign: 'right' }}>P. Costo</th>
                      <th style={{ textAlign: 'right' }}>P. Venta</th>
                      <th style={{ textAlign: 'right' }}>Valorizado</th>
                      <th style={{ textAlign: 'right' }}>V. Proyec.</th>
                      <th>Estado</th>
                    </tr>
                    <tr className="mt-filter-row">
                      <th></th>
                      <th>
                        <input placeholder="Cod…" value={colF.sku || ''}
                          onChange={e => { setColF(c => ({ ...c, sku: e.target.value })); setPage(1); }} />
                      </th>
                      <th>
                        <input placeholder="Nombre…" value={colF.name || ''}
                          onChange={e => { setColF(c => ({ ...c, name: e.target.value })); setPage(1); }} />
                      </th>
                      <th>
                        <input placeholder="Familia…" value={colF.family || ''}
                          onChange={e => { setColF(c => ({ ...c, family: e.target.value })); setPage(1); }} />
                      </th>
                      <th colSpan={7}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedProd.length === 0 ? (
                      <tr className="mt-empty"><td colSpan={11}>Sin productos que coincidan con el filtro</td></tr>
                    ) : pagedProd.map((p, i) => (
                      <React.Fragment key={p.id}>
                        <tr className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                          <td className="td-actions">
                            {p.warehouses?.length > 0 && (
                              <button className="btn-row-edit" onClick={() => toggleProd(p.id)}
                                title="Ver desglose por almacén">
                                {expandedProd[p.id] ? <FiChevronDown /> : <FiChevronRight />}
                              </button>
                            )}
                          </td>
                          <td>
                            <span className="si-barcode">{p.sku || '—'}</span>
                          </td>
                          <td className="si-prod-name">{p.name}</td>
                          <td><span className="si-family-tag">{p.family || '—'}</span></td>
                          <td style={{ fontSize: 12, color: '#475569' }}>{p.unit_measure}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700,
                                       color: p.stock_status === 'SIN_STOCK' ? '#dc2626'
                                            : p.stock_status === 'BAJO' ? '#d97706' : '#1e293b' }}>
                            {fmtQ(p.total_stock)}
                            {!!p.special_condition && p.total_stock_unidad > 0 && (
                              <span style={{ fontSize: 10.5, color: '#64748b', marginLeft: 4 }}>
                                ({p.total_stock_unidad} uds)
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 12 }}>S/ {fmtN(p.cost_price)}</td>
                          <td style={{ textAlign: 'right', fontSize: 12 }}>S/ {fmtN(p.unit_price)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                            {fmtS(p.total_valorizado)}
                          </td>
                          <td style={{ textAlign: 'right', color: '#7c3aed', fontWeight: 600 }}>
                            {fmtS(p.total_venta_proyectada)}
                          </td>
                          <td><BadgeStock status={p.stock_status} /></td>
                        </tr>
                        {expandedProd[p.id] && p.warehouses?.length > 0 && (
                          <tr>
                            <td colSpan={11} style={{ background: '#f0f9ff', padding: '6px 16px 10px 48px', borderBottom: '1px solid #bae6fd' }}>
                              <div className="si-wh-breakdown">
                                {p.warehouses.map(ws => (
                                  <div key={ws.warehouse_id} className="si-wh-breakdown-row">
                                    <span className="si-wh-breakdown-name">{ws.warehouse_name}</span>
                                    <span className="si-wh-breakdown-stock">
                                      {fmtQ(ws.stock)} {p.unit_measure}
                                      {!!p.special_condition && ws.stock_unidad > 0 && (
                                        <span style={{ fontSize: 10.5, color: '#64748b', marginLeft: 4 }}>({ws.stock_unidad} uds)</span>
                                      )}
                                    </span>
                                    <div className="si-wh-breakdown-bar">
                                      <div className="si-wh-breakdown-fill" style={{
                                        width: `${parseFloat(p.total_stock) > 0
                                          ? Math.min(100, (parseFloat(ws.stock) / parseFloat(p.total_stock)) * 100)
                                          : 0}%`
                                      }} />
                                    </div>
                                    <span className="si-wh-breakdown-pct">
                                      {parseFloat(p.total_stock) > 0
                                        ? Math.round((parseFloat(ws.stock) / parseFloat(p.total_stock)) * 100)
                                        : 0}%
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  {filteredProd.length > 0 && (
                    <tfoot>
                      <tr className="si-tfoot">
                        <td colSpan={8} style={{ textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#475569' }}>
                          TOTALES ({filteredProd.length} productos):
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                          {fmtS(filteredProd.reduce((s, p) => s + parseFloat(p.total_valorizado || 0), 0))}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>
                          {fmtS(filteredProd.reduce((s, p) => s + parseFloat(p.total_venta_proyectada || 0), 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════
              TAB 3 — RESUMEN DE MOVIMIENTOS
          ═══════════════════════════════════════════════════ */}
          {activeTab === 'movimientos' && (
            <div className="si-tab-content">
              {/* Filtros de período */}
              <div className="si-filter-bar si-filter-bar-mov">
                <div className="si-filter-group">
                  <span className="si-label">Desde:</span>
                  <input type="date" className="si-input-date" value={movF.from_date}
                    onChange={e => setMovF(f => ({ ...f, from_date: e.target.value }))} />
                  <span className="si-label">Hasta:</span>
                  <input type="date" className="si-input-date" value={movF.to_date}
                    onChange={e => setMovF(f => ({ ...f, to_date: e.target.value }))} />
                  <select className="si-select" value={movF.warehouse_id}
                    onChange={e => setMovF(f => ({ ...f, warehouse_id: e.target.value }))}>
                    <option value="">Todos los almacenes</option>
                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                  <button className="btn-nuevo" onClick={loadMovSummary} disabled={loadingMov}>
                    <FiRefreshCw style={{ fontSize: 12 }} />
                    {loadingMov ? 'Cargando...' : 'Filtrar'}
                  </button>
                </div>
              </div>

              {loadingMov ? (
                <div className="mt-loading">Cargando resumen de movimientos...</div>
              ) : !movSummary ? (
                <div className="mt-loading" style={{ color: '#64748b' }}>
                  Selecciona el período y haz clic en Filtrar.
                </div>
              ) : (
                <>
                  {/* KPIs de movimientos */}
                  <div className="si-kpi-row" style={{ padding: '14px 16px 4px' }}>
                    <div className="si-kpi si-kpi-blue">
                      <div className="si-kpi-icon"><FiBarChart2 /></div>
                      <div>
                        <div className="si-kpi-val">{movSummary.totals?.total_movements || 0}</div>
                        <div className="si-kpi-label">Total Movimientos</div>
                      </div>
                    </div>
                    <div className="si-kpi si-kpi-green">
                      <div className="si-kpi-icon"><FiTrendingUp /></div>
                      <div>
                        <div className="si-kpi-val">{fmtQ(movSummary.totals?.total_entradas)}</div>
                        <div className="si-kpi-label">Total Entradas</div>
                      </div>
                    </div>
                    <div className="si-kpi si-kpi-orange">
                      <div className="si-kpi-icon"><FiAlertCircle /></div>
                      <div>
                        <div className="si-kpi-val">{fmtQ(movSummary.totals?.total_salidas)}</div>
                        <div className="si-kpi-label">Total Salidas</div>
                      </div>
                    </div>
                    <div className="si-kpi si-kpi-purple">
                      <div className="si-kpi-icon"><FiPackage /></div>
                      <div>
                        <div className="si-kpi-val">{fmtS(movSummary.totals?.total_valorizado)}</div>
                        <div className="si-kpi-label">Valorizado Movido</div>
                      </div>
                    </div>
                  </div>

                  {/* Grilla 2 columnas: por tipo  +  top 10 */}
                  <div className="si-mov-grid">
                    {/* Tabla por tipo */}
                    <div>
                      <div className="mt-section-title" style={{ margin: '12px 0 8px 0' }}>
                        <h3>MOVIMIENTOS POR TIPO Y CAUSA</h3>
                        <p>Período: {movSummary.period?.from} — {movSummary.period?.to}</p>
                      </div>
                      <div className="mt-table-wrap">
                        <table className="mt-table">
                          <thead>
                            <tr className="mt-head-row">
                              <th>Tipo</th>
                              <th>Causa</th>
                              <th style={{ textAlign: 'right' }}>Cant.</th>
                              <th style={{ textAlign: 'right' }}>Qty. Movida</th>
                              <th style={{ textAlign: 'right' }}>Valorizado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!movSummary.byType?.length ? (
                              <tr className="mt-empty"><td colSpan={5}>Sin movimientos en el período</td></tr>
                            ) : movSummary.byType.map((r, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                                <td>
                                  <span className={`si-mov-type si-mov-${r.movement_type}`}>
                                    {r.movement_type.toUpperCase()}
                                  </span>
                                </td>
                                <td style={{ fontSize: 11.5 }}>{r.causa || '—'}</td>
                                <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.count}</td>
                                <td style={{ textAlign: 'right' }}>{fmtQ(r.total_qty)}</td>
                                <td style={{ textAlign: 'right', color: '#0369a1', fontWeight: 600 }}>
                                  {fmtS(r.total_valorizado)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Top 10 productos */}
                    <div>
                      <div className="mt-section-title" style={{ margin: '12px 0 8px 0' }}>
                        <h3>TOP 10 PRODUCTOS MÁS MOVIDOS</h3>
                        <p>Por cantidad de movimientos en el período</p>
                      </div>
                      <div className="mt-table-wrap">
                        <table className="mt-table">
                          <thead>
                            <tr className="mt-head-row">
                              <th style={{ width: 28 }}>#</th>
                              <th>Código</th>
                              <th>Producto</th>
                              <th>UM</th>
                              <th style={{ textAlign: 'right' }}>Movim.</th>
                              <th style={{ textAlign: 'right' }}>Entradas</th>
                              <th style={{ textAlign: 'right' }}>Salidas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!movSummary.topProducts?.length ? (
                              <tr className="mt-empty"><td colSpan={7}>Sin datos</td></tr>
                            ) : movSummary.topProducts.map((p, i) => (
                              <tr key={p.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                                <td style={{
                                  textAlign: 'center', fontWeight: 700,
                                  color: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : '#cbd5e1'
                                }}>
                                  {i + 1}
                                </td>
                                <td><span className="si-barcode">{p.sku || '—'}</span></td>
                                <td className="si-prod-name" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.name}
                                </td>
                                <td style={{ fontSize: 11.5, color: '#475569' }}>{p.unit_measure}</td>
                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#0ea5e9' }}>
                                  {p.total_movements}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>
                                  +{fmtQ(p.total_in)}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>
                                  -{fmtQ(p.total_out)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {modalAjuste && (
        <ModalAjusteInicial
          item={modalAjuste.item}
          warehouseId={modalAjuste.warehouseId}
          businessId={businessId}
          onClose={() => setModalAjuste(null)}
          onSaved={() => { setModalAjuste(null); loadData(); }}
        />
      )}

      {modalHistorial && (
        <ModalHistorialMovimientos
          item={modalHistorial.item}
          warehouseId={modalHistorial.warehouseId}
          warehouseName={modalHistorial.warehouseName}
          onClose={() => setModalHistorial(null)}
        />
      )}
    </div>
  );
}
